import { Button, Skeleton, TextInput } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconBrandInstagram, IconBrandTelegram, IconBrandWhatsapp, IconMail, IconMapPin, IconPhone } from "@tabler/icons-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import SettingsShell, { SETTINGS_ROOT, SettingsSection } from "@/modules/settings/SettingsShell";
import classes from "@/modules/settings/settings.module.css";
import profileService from "@/services/merchant";
import { useMutation } from "@tanstack/react-query";
import useGetMerchant from "../../hooks/useGetMerchant";

const url = Yup.string().max(500, "حداکثر ۵۰۰ کاراکتر").test("url", "لینک کامل با https:// وارد کنید", (v) => !v || /^https?:\/\/\S+$/i.test(v));
const schema = Yup.object({
  email: Yup.string().email("ایمیل را درست وارد کنید."),
  publicPhone: Yup.string().test("phone", "فقط رقم (و + اول)، ۸ تا ۱۵ رقم", (v) => !v || /^\+?\d{8,15}$/.test(v)),
  googleBusinessUrl: url,
  neshanUrl: url,
  baladUrl: url,
});

type Values = {
  publicPhone: string; email: string; instagramId: string; instagramLink: string; telegramId: string;
  whatsappNumber: string; googleBusinessUrl: string; neshanUrl: string; baladUrl: string;
};
// Stays RTL so the icon padding sits on the icon side; plaintext lets Latin text and links read left to right.
const ltr = { input: { unicodeBidi: "plaintext" as const, textAlign: "left" as const } };

// «تماس و آدرس»: how customers reach the salon and find it on maps. An emptied field is sent as "" so it is
// cleared; the address itself (with its map) is edited on its own page.
function EditProfileSocialMedia() {
  const { data, isLoading, refetch } = useGetMerchant();
  const save = useMutation({ mutationFn: profileService.updateMerchantSocialMedia });

  const initial = (): Values => ({
    publicPhone: data?.publicPhone ?? "",
    email: data?.email ?? "",
    instagramId: data?.socialMedia?.instagramId ?? "",
    instagramLink: data?.socialMedia?.instagramLink ?? "",
    telegramId: data?.socialMedia?.telegramId ?? "",
    whatsappNumber: data?.socialMedia?.whatsappNumber ?? "",
    googleBusinessUrl: data?.socialMedia?.googleBusinessUrl ?? "",
    neshanUrl: data?.socialMedia?.neshanUrl ?? "",
    baladUrl: data?.socialMedia?.baladUrl ?? "",
  });
  const form = useForm<Values>({ validate: yupResolver(schema), initialValues: initial() });
  useEffect(() => {
    const values = initial();
    form.setValues(values);
    form.resetDirty(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const submit = (values: Values) =>
    save.mutate({ ...values, email: values.email || null }, {
      onSuccess() { notifications.show({ message: "تغییرات ذخیره شد", color: "green" }); form.resetDirty(); refetch(); },
    });

  const area = [data?.district, data?.city, data?.province].filter(Boolean).join("، ");

  return (
    <SettingsShell title="تماس و آدرس" description="راه‌های تماس با سالن روی سایت رزرو و در نقشه‌ها." onSubmit={form.onSubmit(submit)} dirty={form.isDirty()} saving={save.isLoading} onReset={() => form.reset()}>
      {isLoading ? (
        <><Skeleton height={120} radius={14} /><Skeleton height={200} radius={14} /></>
      ) : (
        <>
          <SettingsSection title="تماس" description="شماره‌ی تماس روی سایت شما دکمه‌ی تماس می‌شود؛ با شماره‌ی ورود فرق دارد.">
            <div className={classes.fields}>
              <TextInput label="شماره تماس سالن" placeholder="02188776655" leftSection={<IconPhone size={18} />} styles={ltr} {...form.getInputProps("publicPhone")} />
              <TextInput label="ایمیل" type="email" placeholder="salon@example.com" leftSection={<IconMail size={18} />} styles={ltr} {...form.getInputProps("email")} />
            </div>
          </SettingsSection>

          <SettingsSection title="آدرس" description="آدرس، محله و کد پستی؛ همراه با موقعیت روی نقشه.">
            <div className={classes.address}>
              <div>
                {data?.address ? <>{data.address}{data.addressDetail ? `، ${data.addressDetail}` : ""}</> : "آدرسی ثبت نشده است"}
                {(area || data?.postalCode) && <span>{area}{data?.postalCode ? `${area ? " · " : ""}کد پستی ${data.postalCode}` : ""}</span>}
              </div>
              <Button variant="default" size="sm" component={Link} to={`${SETTINGS_ROOT}/merchantAddress`}>ویرایش آدرس</Button>
            </div>
          </SettingsSection>

          <SettingsSection title="شبکه‌های اجتماعی" description="روی سایت و در پیامک‌ها استفاده می‌شوند.">
            <div className={classes.fields}>
              <TextInput label="آیدی اینستاگرام" placeholder="rose.salon" leftSection={<IconBrandInstagram size={18} />} styles={ltr} {...form.getInputProps("instagramId")} />
              <TextInput label="لینک صفحه‌ی اینستاگرام" placeholder="https://instagram.com/rose.salon" leftSection={<IconBrandInstagram size={18} />} styles={ltr} {...form.getInputProps("instagramLink")} />
              <TextInput label="تلگرام" placeholder="@rose_salon" leftSection={<IconBrandTelegram size={18} />} styles={ltr} {...form.getInputProps("telegramId")} />
              <TextInput label="شماره‌ی واتساپ" placeholder="09120000000" leftSection={<IconBrandWhatsapp size={18} />} styles={ltr} {...form.getInputProps("whatsappNumber")} />
            </div>
          </SettingsSection>

          <SettingsSection title="روی نقشه‌ها" description="لینک صفحه‌ی سالن در گوگل‌مپ، نشان و بلد؛ به دیده شدن در جستجو کمک می‌کند.">
            <TextInput label="Google Business / گوگل‌مپ" placeholder="https://maps.app.goo.gl/..." leftSection={<IconMapPin size={18} />} styles={ltr} {...form.getInputProps("googleBusinessUrl")} />
            <div className={classes.fields}>
              <TextInput label="نشان" placeholder="https://nshn.ir/..." leftSection={<IconMapPin size={18} />} styles={ltr} {...form.getInputProps("neshanUrl")} />
              <TextInput label="بلد" placeholder="https://balad.ir/p/..." leftSection={<IconMapPin size={18} />} styles={ltr} {...form.getInputProps("baladUrl")} />
            </div>
          </SettingsSection>
        </>
      )}
    </SettingsShell>
  );
}

export default EditProfileSocialMedia;
